import { Activity, BellRing, CheckCircle2, CloudCog, RefreshCw, Smartphone, TimerReset, Workflow } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { adminRequest } from './api';
import type { Application, AsyncJob, FirebaseProject, NotificationDelivery, PushOverview } from './types';

const EMPTY: PushOverview = {
  total: 0, queued: 0, active: 0, completed: 0, failed: 0,
  activeInstallations: 0, delivered: 0, dead: 0, pending: 0,
};

function compact(value: number): string {
  return new Intl.NumberFormat('en', { notation: value >= 10_000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value);
}

function dateTime(value: string | null): string {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function Status({ value }: { value: string }) {
  return <span className={`status status--${value}`}><span />{value}</span>;
}

export function Operations({
  applications,
  filterApp,
  onError,
}: {
  applications: Application[];
  filterApp: string;
  onError: (error: unknown) => void;
}) {
  const [overview, setOverview] = useState(EMPTY);
  const [jobs, setJobs] = useState<AsyncJob[]>([]);
  const [deliveries, setDeliveries] = useState<NotificationDelivery[]>([]);
  const [selectedApp, setSelectedApp] = useState(filterApp || applications[0]?.id || '');
  const [firebase, setFirebase] = useState<FirebaseProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function loadActivity() {
    setLoading(true);
    const query = filterApp ? `&appId=${encodeURIComponent(filterApp)}` : '';
    try {
      const [nextOverview, nextJobs, nextDeliveries] = await Promise.all([
        adminRequest<PushOverview>(`/push/overview?${filterApp ? `appId=${encodeURIComponent(filterApp)}` : ''}`),
        adminRequest<AsyncJob[]>(`/jobs?limit=100${query}`),
        adminRequest<NotificationDelivery[]>(`/notifications?limit=100${query}`),
      ]);
      setOverview({ ...EMPTY, ...nextOverview });
      setJobs(nextJobs);
      setDeliveries(nextDeliveries);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }

  async function loadFirebase(appId: string) {
    if (!appId) { setFirebase(null); return; }
    try { setFirebase(await adminRequest<FirebaseProject | null>(`/applications/${appId}/firebase`)); }
    catch (error) { onError(error); }
  }

  useEffect(() => { void loadActivity(); }, [filterApp]);
  useEffect(() => {
    const next = filterApp || selectedApp || applications[0]?.id || '';
    if (next !== selectedApp) setSelectedApp(next);
  }, [applications, filterApp]);
  useEffect(() => { void loadFirebase(selectedApp); }, [selectedApp]);

  async function saveFirebase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setSaving(true);
    try {
      const next = await adminRequest<FirebaseProject>(`/applications/${selectedApp}/firebase`, {
        method: 'PUT',
        body: JSON.stringify({ projectId: data.get('projectId'), credentialId: data.get('credentialId'), status: data.get('status') }),
      });
      setFirebase(next);
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  }

  const deliveryRate = overview.delivered + overview.dead > 0
    ? overview.delivered / (overview.delivered + overview.dead) * 100
    : 100;
  return <>
    <div className="section-title">
      <div><p className="eyebrow">Push-to-Sync</p><h2>Async work, visible end to end.</h2><p>Observe job state, device reachability, retries, and FCM delivery without exposing generated output in notifications.</p></div>
      <button className="button" onClick={() => void loadActivity()} disabled={loading}><RefreshCw size={17} className={loading ? 'spin' : ''} />Refresh</button>
    </div>
    <section className="metrics operations-metrics">
      <article><div className="metric-icon metric-icon--blue"><Workflow /></div><span>Async jobs</span><strong>{compact(overview.total)}</strong><small>{overview.active} active · {overview.queued} queued</small></article>
      <article><div className="metric-icon metric-icon--green"><Smartphone /></div><span>Installations</span><strong>{compact(overview.activeInstallations)}</strong><small>Active encrypted device tokens</small></article>
      <article><div className="metric-icon metric-icon--coral"><BellRing /></div><span>Push delivery</span><strong>{deliveryRate.toFixed(1)}<i>%</i></strong><small>{overview.pending} pending · {overview.dead} dead</small></article>
      <article><div className="metric-icon metric-icon--gold"><TimerReset /></div><span>Completed</span><strong>{compact(overview.completed)}</strong><small>{overview.failed} failed after retries</small></article>
    </section>
    <section className="operations-grid">
      <article className="panel firebase-card">
        <div className="panel-head"><div><p className="eyebrow">Per-app transport</p><h3>Firebase project</h3></div><CloudCog size={20} /></div>
        {selectedApp ? <form onSubmit={saveFirebase}>
          <label>Application<select value={selectedApp} onChange={(event) => setSelectedApp(event.target.value)} disabled={Boolean(filterApp)}>{applications.map((app) => <option value={app.id} key={app.id}>{app.name}</option>)}</select></label>
          <label>Firebase project ID<input name="projectId" key={firebase?.projectId || selectedApp} defaultValue={firebase?.projectId || ''} placeholder="your-firebase-project" required /></label>
          <label>FCM credential ID<input name="credentialId" key={firebase?.credentialId || selectedApp} defaultValue={firebase?.credentialId || ''} placeholder="Encrypted credential UUID" required /></label>
          <label>Status<select name="status" key={firebase?.status || 'active'} defaultValue={firebase?.status || 'active'}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
          <button className="button button--primary" disabled={saving}>{saving ? <RefreshCw className="spin" size={16} /> : <CheckCircle2 size={16} />}Save transport</button>
          <small>The selected app-scoped service account must belong to this Firebase project.</small>
        </form> : <div className="empty-state"><span><CloudCog /></span><strong>No application</strong><p>Register an application before configuring delivery.</p></div>}
      </article>
      <article className="panel delivery-health">
        <div className="panel-head"><div><p className="eyebrow">Delivery health</p><h3>Outbox posture</h3></div><Activity size={20} /></div>
        <div className="delivery-score"><strong>{compact(overview.delivered)}</strong><span>events delivered</span><div><i style={{ width: `${deliveryRate}%` }} /></div><small>Terminal success rate {deliveryRate.toFixed(1)}%</small></div>
        <dl><div><dt>Pending / retrying</dt><dd>{overview.pending}</dd></div><div><dt>Dead-lettered</dt><dd>{overview.dead}</dd></div><div><dt>Failed jobs</dt><dd>{overview.failed}</dd></div></dl>
      </article>
    </section>
    <section className="panel request-ledger operations-table">
      <div className="panel-head"><div><p className="eyebrow">Job state machine</p><h3>Recent asynchronous jobs</h3></div><span className="record-count">{jobs.length} records</span></div>
      {jobs.length ? <div className="table-wrap"><table><thead><tr><th>Job</th><th>Boundary</th><th>Feature</th><th>Model</th><th>Attempt</th><th>Sync events</th><th>Status</th></tr></thead><tbody>{jobs.map((job) => <tr key={job.id}><td><strong className="request-id">{job.id.slice(0, 8)}</strong><small>{dateTime(job.createdAt)} · v{job.version}</small></td><td><strong>{applications.find((app) => app.id === job.appId)?.name || job.appId.slice(0, 8)}</strong><small>{job.tenantId} / {job.subjectId}</small></td><td><code>{job.feature}</code><small>{job.clientRoute || 'Polling only'}</small></td><td>{job.provider}<small>{job.model}</small></td><td>{job.attempts} / {job.maxAttempts}</td><td>{job.deliveredCount} / {job.deliveryCount}</td><td><Status value={job.status} />{job.errorCode && <small className="error-code">{job.errorCode}</small>}</td></tr>)}</tbody></table></div> : <div className="empty-state"><span><Workflow /></span><strong>No asynchronous jobs</strong><p>Jobs submitted through <code>/v1/jobs</code> will appear here.</p></div>}
    </section>
    <section className="panel request-ledger operations-table">
      <div className="panel-head"><div><p className="eyebrow">Notification outbox</p><h3>Recent delivery attempts</h3></div><span className="record-count">{deliveries.length} records</span></div>
      {deliveries.length ? <div className="table-wrap"><table><thead><tr><th>Event</th><th>Application</th><th>Installation</th><th>Platform</th><th>Attempt</th><th>Created</th><th>Status</th></tr></thead><tbody>{deliveries.map((delivery) => <tr key={delivery.id}><td><strong>{delivery.eventType}</strong><small>{delivery.jobId.slice(0, 8)} · v{delivery.jobVersion}</small></td><td>{delivery.appName}</td><td><code>{delivery.installationId}</code></td><td>{delivery.platform}</td><td>{delivery.attempts}</td><td>{dateTime(delivery.createdAt)}</td><td><Status value={delivery.status} />{delivery.errorCode && <small className="error-code">{delivery.errorCode}</small>}</td></tr>)}</tbody></table></div> : <div className="empty-state"><span><BellRing /></span><strong>No push deliveries</strong><p>Polling remains available even when a job has no registered installation.</p></div>}
    </section>
  </>;
}
